import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

export const PullCustomsNotificationsSchema = z
  .object({
    system: z.string().min(1, { message: 'System is required' }),
    declarantId: z.string().length(24, { message: 'Invalid declarant id' }),
    lrn: z.string().optional().default(''),
    mrn: z.string().optional().default(''),
    transitGroup: z.string().optional().default(''),
    correlationId: z.string().optional().default(''),
  })
  .refine((data) => data.lrn?.length || data.correlationId?.length || data.mrn?.length, {
    message: 'LRN, MRN or correlation is required',
  })
  .refine((data) => !(data.mrn?.length && ['ncts', 'delta-t'].includes(data.system) && !data.transitGroup?.length), {
    message: 'Transit group is required',
  });

export type PullCustomsNotificationsRequest = z.infer<typeof PullCustomsNotificationsSchema>;

export const UpdateCorrelationIdSchema = z
  .object({
    system: z.string().min(1),
    oldCorrelationId: z.string().optional().default(''),
    lrn: z.string().optional().default(''),
    mrn: z.string().optional().default(''),
    transitGroup: z.string().optional().default(''),
    newCorrelationId: z.string().min(1),
  })
  .refine((data) => data.oldCorrelationId?.length || data.lrn?.length || data.mrn?.length, {
    message: 'LRN, MRN or correlation is required',
  })
  .refine((data) => !(data.mrn?.length && data.system === 'ncts' && !data.transitGroup?.length), {
    message: 'Transit group is required',
  });
export type UpdateCorrelationIdRequest = z.infer<typeof UpdateCorrelationIdSchema>;

export const CustomsSyncSchema = z.object({
  system: z.string().min(1),
  entityId: z.string().min(1),
  declarantId: z.string().length(24, { message: 'Invalid declarant id' }),
});
export type CustomsSyncRequest = z.infer<typeof CustomsSyncSchema>;

export const CustomsNotificationResultSchema = z.object({
  notifications: z.array(
    z.object({
      href: z.string(),
      xml: z.string(),
      format: z.string().optional(),
      code: z.string(),
      date: z.string(),
      correlationId: z.string(),
      isInEvents: z.boolean().optional(),
    }),
  ),
  correlationId: z.string().optional(),
  customsSyncAvailable: z.boolean().optional(),
  entityId: z.string().optional(),
});

export type CustomsNotificationResult = z.infer<typeof CustomsNotificationResultSchema>;

export const UpdateCorrelationIdResultSchema = z.object({
  message: z.string(),
});

export type UpdateCorrelationIdResult = z.infer<typeof UpdateCorrelationIdResultSchema>;

export const customsNotificationsContract = c.router({
  pullCustomsNotifications: {
    method: 'POST',
    path: '/admin/customs-notifications',
    body: PullCustomsNotificationsSchema,
    responses: { 200: CustomsNotificationResultSchema },
  },
  updateCorrelationId: {
    method: 'POST',
    path: '/admin/update-correlation-id',
    body: UpdateCorrelationIdSchema,
    responses: { 200: UpdateCorrelationIdResultSchema },
  },
  customsSync: {
    method: 'POST',
    path: '/admin/customs-sync',
    body: CustomsSyncSchema,
    responses: { 200: UpdateCorrelationIdResultSchema },
  },
});
